import { useCallback, useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { MyContext } from '../../../context';
import MappingList from '../../../components/MappingList';
import { listProductPricingTiers, createPricingTier, removePricingTier, updateProduct } from '../../../utils/queries';
import { formatCurrency } from '../../../utils/format';

const formatCents = (cents) => (cents == null ? '—' : formatCurrency(cents));

const VOLUME_COLUMNS = [
	{ key: 'minQuantity', header: 'Min Quantity' },
	{ key: 'pointCost', header: 'Point Cost' },
];
const VOLUME_ADD_FIELDS = [
	{ key: 'minQuantity', label: 'Min Quantity', required: true },
	{ key: 'pointCost', label: 'Point Cost', required: true },
];

// Currency stores can have Pace-synced tiers (source: 'pace', see
// runPricingSync in pi_ecommerce_api's syncWorkers.ts) alongside manual ones.
// unitPriceCents is the dollar-priced counterpart to pointCost, and source is
// surfaced so a synced tier's ERP origin (and its read-only-ness, see below)
// is visible rather than looking like any other manually-entered row.
const VOLUME_COLUMNS_CURRENCY = [
	{ key: 'minQuantity', header: 'Min Quantity' },
	{ key: 'unitPriceCents', header: 'Unit Price', render: formatCents },
	{ key: 'source', header: 'Source', render: (source) => (source === 'pace' ? 'ERP' : 'Manual') },
];
const VOLUME_ADD_FIELDS_CURRENCY = [
	{ key: 'minQuantity', label: 'Min Quantity', required: true },
	{ key: 'unitPriceCents', label: 'Unit Price (cents)', required: true },
];

function Divider() {
	return <hr className='my-8 border-gray-200' />;
}

// Currency stores' base (quantity-1) price is entirely ERP-sourced (Pace
// Estimate price breaks or QuantityPriceDiscount, see runPricingSync) --
// display-only, never hand-edited here.
function UnitPriceSection({ product }) {
	return (
		<div className='mb-2'>
			<p className='text-xs text-gray-500'>Unit Price</p>
			<p className='text-sm'>{formatCents(product.unitPriceCents)}</p>
			{/* <p className='text-xs text-gray-400 mt-1'>
				{product.unitPriceCents == null ? 'Not set — ' : ''}Synced from Pace.
			</p> */}
		</div>
	);
}

// Loyalty stores have no ERP-sourced base price -- Point Cost is entirely
// manual, and (since it moved off the Details tab) this is the only place to
// set it.
function PointCostSection({ product, onSaved }) {
	const [points, setPoints] = useState(product.pointCost ?? '');
	const [saving, setSaving] = useState(false);

	const handleSave = async (e) => {
		e.preventDefault();
		setSaving(true);
		const result = await updateProduct(product.productId, {
			pointCost: points === '' ? null : Number(points),
		});
		setSaving(false);
		if (!result?.success) {
			toast.error(result?.error?.message || 'Failed to save.');
			return;
		}
		toast.success('Saved.');
		onSaved?.(result.data);
	};

	return (
		<form onSubmit={handleSave} className='mb-2 flex items-end gap-3'>
			<div className='flex flex-col'>
				<label>Point Cost</label>
				<input
					type='number'
					min='0'
					className='form-input w-32'
					value={points}
					onChange={(e) => setPoints(e.target.value)}
				/>
				{product.pointCost == null && <p className='text-xs text-gray-400 mt-1'>Not set</p>}
			</div>
			<button type='submit' disabled={saving} className='btn-primary h-10'>
				{saving ? 'Saving...' : 'Save'}
			</button>
		</form>
	);
}

export default function PricingTab({ product, onSaved }) {
	const { stores } = useContext(MyContext);
	// Loyalty Points is mutually exclusive with every other payment method
	// (see createStoreLoyaltyProgram in stores.service.ts) -- a store without
	// it enabled is a currency store, regardless of currencyCode (a separate,
	// display-only field).
	const isCurrencyStore = !stores.find((s) => s.storeId === product.storeId)?.hasLoyaltyPoints;

	const list = useCallback(() => listProductPricingTiers(product.productId), [product.productId]);
	const add = useCallback((fields) => createPricingTier(product.productId, fields), [product.productId]);

	return (
		<div>
			{isCurrencyStore ? (
				<UnitPriceSection product={product} />
			) : (
				<PointCostSection product={product} onSaved={onSaved} />
			)}

			<Divider />

			<h3 className='text-sm font-medium mb-3'>Volume-Based Pricing</h3>
			{isCurrencyStore && (
				<p className='text-xs text-gray-400 mb-3'>Synced from Pace — not editable here.</p>
			)}
			<MappingList
				list={list}
				add={add}
				remove={removePricingTier}
				columns={isCurrencyStore ? VOLUME_COLUMNS_CURRENCY : VOLUME_COLUMNS}
				addFields={isCurrencyStore ? VOLUME_ADD_FIELDS_CURRENCY : VOLUME_ADD_FIELDS}
				idKey='volumeTierId'
				emptyHeading='No volume pricing tiers found'
				collapsibleAdd
				// Adding a tier by hand only makes sense for loyalty stores --
				// currency stores' volume pricing is entirely ERP-sourced (see
				// runPricingSync), so canEdit=false hides both the add form and
				// every row's Remove button for them.
				canEdit={!isCurrencyStore}
				// Belt-and-suspenders for loyalty stores: a Pace-synced tier
				// shouldn't normally exist there, but if one ever did, it's
				// overwritten/deactivated by the next sync run rather than staying
				// removed, so don't offer a Remove button for it either.
				canRemoveRow={(row) => row.source !== 'pace'}
			/>
		</div>
	);
}
